/**
 * AllInventoryTable
 * One unified table showing all 6 inventory types merged together.
 * Same visual style as InventoryTable — aggregate rows + expandable lot breakdown.
 */

import React, { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Collapse, Chip, IconButton, Stack, alpha, useTheme,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, CircularProgress, Tooltip
} from '@mui/material';
import {
  ExpandMore, ExpandLess, Edit as EditIcon, Description as DescriptionIcon,
  Receipt as ReceiptIcon, AssignmentTurnedIn as AssignmentTurnedInIcon,
  Science as ScienceIcon, LocalShipping as LocalShippingIcon,
  Warning as WarningIcon, CheckCircle as CheckCircleIcon,
  Search as SearchIcon, Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { formatDate } from '../shared/inventoryUtils';
import EditLotDialog from '../shared/EditLotDialog';
import EditReorderDialog from '../shared/EditReorderDialog';
import { supabase } from '../../../../supabaseClient';

const LOT_BATCH_DEFAULT_ROWS = 12;

// Per-type chip colours
const TYPE_STYLES = {
  products:  { label: 'Products',  color: '#1565c0', bg: '#e3f2fd' },
  chemicals: { label: 'Chemicals', color: '#6a1b9a', bg: '#f3e5f5' },
  caps:      { label: 'Caps',      color: '#e65100', bg: '#fff3e0' },
  bottles:   { label: 'Bottles',   color: '#00695c', bg: '#e0f2f1' },
  labels:    { label: 'Labels',    color: '#2e7d32', bg: '#e8f5e9' },
  supplies:  { label: 'Supplies',  color: '#b71c1c', bg: '#ffebee' },
};

function cleanNotes(notes) {
  if (!notes) return '';
  return notes.split(';').map(s => s.trim()).filter(seg =>
    !seg.match(/^Adjusted to \d+/) &&
    !seg.startsWith('Transfer merge - Added') &&
    !seg.startsWith('Transfer from location - Ref:') &&
    !seg.startsWith('Adjustment created lot/batch - Ref:') &&
    !seg.match(/^Merged transfer from /)
  ).join('; ');
}

/* ── Quantity helpers ── */
function ItemQuantity({ item }) {
  const cfg = item._config;
  if (cfg.hasUnitConfig) {
    const totalBase  = item.total_base_units ?? item.total_quantity ?? 0;
    const totalCases = (item.lots || []).reduce((sum, lot) =>
      sum + Math.floor((lot.total_base_units ?? 0) / (lot.units_per_case || 1)), 0);
    return (
      <>
        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
          {totalCases.toLocaleString()} cases
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          {totalBase.toLocaleString()} total units
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          {item.lots?.length ?? 0} lots
        </Typography>
      </>
    );
  }
  if (cfg.hasUnitOfMeasure) {
    return (
      <>
        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
          {(item.total_quantity ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {item.unit_type || 'units'}
        </Typography>
        {item.total_base_quantity != null && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {typeof item.total_base_quantity === 'number'
              ? item.total_base_quantity.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })
              : item.total_base_quantity} {item.unit_measure || ''} total
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          {item.lots?.length ?? 0} lots
        </Typography>
      </>
    );
  }
  return (
    <>
      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
        {(item.total_quantity ?? 0).toLocaleString()} units
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
        {item.lots?.length ?? 0} lots
      </Typography>
    </>
  );
}

function LotQuantity({ lotItem, cfg, parentItem }) {
  if (cfg.hasUnitConfig) {
    return (
      <>
        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
          {(lotItem.current_quantity ?? lotItem.total_base_units ?? 0).toLocaleString()} units
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          {(lotItem.case_quantity ?? lotItem.total_cases ?? 0).toLocaleString()} cases
        </Typography>
      </>
    );
  }
  if (cfg.hasUnitOfMeasure) {
    const unitType    = lotItem.unit_type    || parentItem?.unit_type    || 'units';
    const unitMeasure = lotItem.unit_measure || parentItem?.unit_measure || '';
    const unitQty     = lotItem.current_unit_quantity ?? lotItem.current_quantity ?? 0;
    const totalQty    = lotItem.current_total_quantity;
    return (
      <>
        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
          {typeof unitQty === 'number'
            ? unitQty.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })
            : unitQty} {unitType}
        </Typography>
        {totalQty != null && unitMeasure && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {typeof totalQty === 'number'
              ? totalQty.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })
              : totalQty} {unitMeasure} total
          </Typography>
        )}
      </>
    );
  }
  return (
    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>
      {(lotItem.current_quantity ?? 0).toLocaleString()} units
    </Typography>
  );
}

/* ── shared cell sx ── */
const thCellSx = {
  fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white',
  borderBottom: '2px solid', borderColor: 'primary.main',
  color: 'primary.main', py: 2, px: 3
};
const tdCellSx = { py: 3, px: 3 };

export default function AllInventoryTable({ data = [], onRefresh, brands = [], onBrandsChange, canEdit = true, canDelete = true }) {
  const theme = useTheme();
  const [expandedRows, setExpandedRows]           = useState({});
  const [lotSearchTerms, setLotSearchTerms]       = useState({});
  const [lotPagination, setLotPagination]         = useState({});
  const [editLotOpen, setEditLotOpen]             = useState(false);
  const [editReorderOpen, setEditReorderOpen]     = useState(false);
  const [selectedItem, setSelectedItem]           = useState(null);
  const [deleteLotOpen, setDeleteLotOpen]         = useState(false);
  const [deleteAggOpen, setDeleteAggOpen]         = useState(false);
  const [itemToDelete, setItemToDelete]           = useState(null);
  const [deleting, setDeleting]                   = useState(false);

  const rowKey = (item) => `${item._inventoryType}-${item.item_id}-${item.variant_id ?? 'nv'}-${item.location_id}`;

  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

  /* ── Brand reorder ── */
  const sortedBrandsForReorder = [...brands].sort((a, b) => {
    const oA = a.display_order ?? 9999;
    const oB = b.display_order ?? 9999;
    return oA !== oB ? oA - oB : a.name.localeCompare(b.name);
  });

  const handleBrandReorder = async (brandId, direction) => {
    const list = [...sortedBrandsForReorder];
    const cur = list.findIndex(b => b.id === brandId);
    if (cur === -1) return;
    if (direction === 'up' && cur === 0) return;
    if (direction === 'down' && cur === list.length - 1) return;
    const tgt = direction === 'up' ? cur - 1 : cur + 1;
    [list[cur], list[tgt]] = [list[tgt], list[cur]];
    const updated = list.map((b, i) => ({ ...b, display_order: i }));
    if (onBrandsChange) onBrandsChange(updated);
    await Promise.all(updated.map(b =>
      supabase.from('brands').update({ display_order: b.display_order }).eq('id', b.id)
    ));
  };

  /* ── delete helpers ── */
  const handleDeleteLot = (lotItem, cfg) => {
    setItemToDelete({ ...lotItem, _cfg: cfg });
    setDeleteLotOpen(true);
  };
  const handleDeleteAgg = (item) => {
    setItemToDelete({ ...item, _cfg: item._config });
    setDeleteAggOpen(true);
  };

  const updateAggregateAfterDelete = async (lotItem) => {
    const cfg = lotItem._cfg;
    if (cfg.hasUnitConfig) {
      await supabase.rpc('recalculate_product_inventory', {
        p_company_id: lotItem.company_id,
        p_product_id: lotItem[cfg.itemIdField],
        p_variant_id: lotItem.variant_id || null,
        p_location_id: lotItem.location_id
      });
      return;
    }
    const { data: rem } = await supabase
      .from(cfg.itemsTable)
      .select('current_quantity')
      .eq('company_id', lotItem.company_id)
      .eq(cfg.itemIdField, lotItem[cfg.itemIdField])
      .eq('location_id', lotItem.location_id)
      .eq('is_active', true)
      .gt('current_quantity', 0);
    const total = (rem || []).reduce((s, i) => s + (i.current_quantity || 0), 0);
    if (total > 0) {
      await supabase.from(cfg.inventoryTable).update({
        current_quantity: total,
        [cfg.lastUpdatedField || 'last_updated']: new Date().toISOString()
      }).eq('company_id', lotItem.company_id)
        .eq(cfg.itemIdField, lotItem[cfg.itemIdField])
        .eq('location_id', lotItem.location_id);
    } else {
      await supabase.from(cfg.inventoryTable).delete()
        .eq('company_id', lotItem.company_id)
        .eq(cfg.itemIdField, lotItem[cfg.itemIdField])
        .eq('location_id', lotItem.location_id);
    }
  };

  const confirmDeleteLot = async () => {
    if (!itemToDelete) return;
    const cfg = itemToDelete._cfg;
    try {
      setDeleting(true);
      // Cleanup inactive duplicates — scope to variant for products
      let cleanupQ = supabase.from(cfg.itemsTable).delete()
        .eq('company_id', itemToDelete.company_id)
        .eq(cfg.itemIdField, itemToDelete[cfg.itemIdField])
        .eq('location_id', itemToDelete.location_id)
        .eq('lot_number', itemToDelete.lot_number)
        .eq('batch_number', itemToDelete.batch_number)
        .eq('is_active', false);
      if (cfg.hasUnitConfig) {
        if (itemToDelete.variant_id) cleanupQ = cleanupQ.eq('variant_id', itemToDelete.variant_id);
        else cleanupQ = cleanupQ.is('variant_id', null);
      }
      await cleanupQ;

      let srcLot = itemToDelete.lot_number || null;
      let srcBatch = itemToDelete.batch_number || null;
      if (itemToDelete.source_transaction_id) {
        const { data: tx } = await supabase
          .from(cfg.transactionsTable).select('lot_number, batch_number')
          .eq('id', itemToDelete.source_transaction_id).single();
        if (tx) { srcLot = tx.lot_number || srcLot; srcBatch = tx.batch_number || srcBatch; }
        await supabase.from(cfg.transactionsTable).delete().eq('id', itemToDelete.source_transaction_id);
      }

      const qty      = Math.abs(parseFloat(itemToDelete.current_quantity) || 0);
      const totalQty = Math.abs(parseFloat(itemToDelete.current_total_quantity) || qty);
      const name     = itemToDelete.item_name || 'item';
      const lotParts = [srcLot && `Lot: ${srcLot}`, srcBatch && `Batch: ${srcBatch}`].filter(Boolean).join(', ');
      const txFields = cfg.hasUnitOfMeasure
        ? { unit_quantity: -qty, total_quantity: -totalQty }
        : { quantity: -qty };
      await supabase.from(cfg.transactionsTable).insert({
        company_id: itemToDelete.company_id,
        [cfg.itemIdField]: itemToDelete[cfg.itemIdField],
        transaction_type: 'inventory_deletion',
        ...txFields,
        location_id: itemToDelete.location_id,
        lot_number: srcLot, batch_number: srcBatch,
        transaction_date: new Date().toISOString(),
        notes: `Lot deleted — ${name}${lotParts ? ` (${lotParts})` : ''}`
      });
      const { error } = await supabase.from(cfg.itemsTable).delete().eq('id', itemToDelete.id);
      if (error) throw error;
      await updateAggregateAfterDelete(itemToDelete);
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error deleting lot:', err);
    } finally {
      setDeleting(false); setDeleteLotOpen(false); setItemToDelete(null);
    }
  };

  const confirmDeleteAgg = async () => {
    if (!itemToDelete) return;
    const cfg = itemToDelete._cfg;
    try {
      setDeleting(true);
      const cid       = itemToDelete.company_id;
      const itemId    = itemToDelete.item_id;
      const lid       = itemToDelete.location_id;
      const variantId = itemToDelete.variant_id || null;

      // Delete each lot item by its primary key ID (same method as individual lot delete)
      const lotsToDelete = itemToDelete.lots || [];
      for (const lot of lotsToDelete) {
        const { error: lotErr } = await supabase.from(cfg.itemsTable).delete().eq('id', lot.id);
        if (lotErr) throw lotErr;
      }

      // Also delete any remaining items via compound key (handles items not in lots array)
      let remainingQ = supabase.from(cfg.itemsTable).delete()
        .eq('company_id', cid)
        .eq(cfg.itemIdField, itemId)
        .eq('location_id', lid);
      if (cfg.hasUnitConfig) {
        remainingQ = variantId ? remainingQ.eq('variant_id', variantId) : remainingQ.is('variant_id', null);
      }
      await remainingQ;

      // Fetch aggregate record ID, then delete by primary key
      let aggSelectQ = supabase.from(cfg.inventoryTable).select('id')
        .eq('company_id', cid)
        .eq(cfg.itemIdField, itemId)
        .eq('location_id', lid);
      if (cfg.hasUnitConfig) {
        aggSelectQ = variantId ? aggSelectQ.eq('variant_id', variantId) : aggSelectQ.is('variant_id', null);
      }
      const { data: aggRow } = await aggSelectQ.maybeSingle();
      if (aggRow) {
        const { error: aggErr } = await supabase.from(cfg.inventoryTable).delete().eq('id', aggRow.id);
        if (aggErr) throw aggErr;
      }

      // Log deletion transaction
      const totalQty = Math.abs(parseFloat(itemToDelete.total_quantity) || 0);
      const txFields = cfg.hasUnitOfMeasure
        ? { unit_quantity: -totalQty, total_quantity: -(Math.abs(parseFloat(itemToDelete.total_base_quantity) || totalQty)) }
        : cfg.hasUnitConfig
          ? { quantity_change: -(Math.abs(parseFloat(itemToDelete.total_base_units) || totalQty)) }
          : { quantity: -totalQty };
      await supabase.from(cfg.transactionsTable).insert({
        company_id: cid,
        [cfg.itemIdField]: itemId,
        ...(cfg.hasUnitConfig && variantId ? { variant_id: variantId } : {}),
        transaction_type: 'inventory_deletion',
        ...txFields,
        location_id: lid,
        transaction_date: new Date().toISOString(),
        notes: `All inventory deleted — ${itemToDelete.item_name || 'item'} (${lotsToDelete.length} lot${lotsToDelete.length !== 1 ? 's' : ''})`
      });

      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error deleting aggregate:', err);
      alert('Failed to delete: ' + err.message);
    } finally {
      setDeleting(false); setDeleteAggOpen(false); setItemToDelete(null);
    }
  };

  const viewDocument = async (bucket, filePath) => {
    if (!bucket || !filePath) return;
    try {
      const { data } = await supabase.storage.from(bucket).createSignedUrl(filePath, 3600);
      if (data?.signedUrl) window.open(data.signedUrl, '_blank');
    } catch (err) { console.error('Error viewing doc:', err); }
  };

  const renderDocuments = (lotItem, cfg) => {
    const trans = lotItem.source_transaction || {};
    const sb    = cfg.storageBuckets || {};
    const defs  = [
      { key: 'invoice', label: 'INV',  bucket: sb.invoices,       pathKey: 'invoice_file_path',          color: 'primary',   icon: <ReceiptIcon fontSize="small" /> },
      { key: 'po',      label: 'PO',   bucket: sb.po,             pathKey: 'po_file_path',                color: 'secondary', icon: <DescriptionIcon fontSize="small" />, sx: { bgcolor: '#00897b', color: 'white', '& .MuiChip-icon': { color: 'white' } } },
      { key: 'coa',     label: 'COA',  bucket: sb.coa,            pathKey: 'coa_file_path',               color: 'success',   icon: <AssignmentTurnedInIcon fontSize="small" /> },
      { key: 'msds',    label: 'MSDS', bucket: sb.msds,           pathKey: 'msds_file_path',              color: 'warning',   icon: <ScienceIcon fontSize="small" /> },
      { key: 'bol',     label: 'BOL',  bucket: sb.bol,            pathKey: 'bol_file_path',               color: 'info',      icon: <LocalShippingIcon fontSize="small" /> },
      { key: 'sds',     label: 'SDS',  bucket: sb.sds,            pathKey: 'sds_file_path',               color: 'warning',   icon: <DescriptionIcon fontSize="small" /> },
      { key: 'prod',    label: 'PROD', bucket: sb.production_doc, pathKey: 'production_doc_file_path',    color: undefined,   icon: <DescriptionIcon fontSize="small" />, sx: { bgcolor: '#9c6fce', color: 'white', '& .MuiChip-icon': { color: 'white' } } },
    ];
    const available = defs.filter(d => d.bucket && (trans[d.pathKey] || lotItem[d.pathKey]));
    if (!available.length) {
      return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>No documents</Typography>;
    }
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center">
        {available.map(d => (
          <Chip key={d.key} label={d.label} size="small"
            color={d.sx ? undefined : d.color} icon={d.icon} clickable
            onClick={() => viewDocument(d.bucket, trans[d.pathKey] || lotItem[d.pathKey])}
            sx={{ fontSize: '0.75rem', height: 24, ...(d.sx || {}) }} />
        ))}
      </Stack>
    );
  };

  // Count products per brand for "Showing X of Y items"
  const brandCounts = {};
  data.forEach(item => {
    if (item._inventoryType === 'products') {
      const b = item.brand_name || 'Unbranded';
      brandCounts[b] = (brandCounts[b] || 0) + 1;
    }
  });

  if (!data.length) {
    return (
      <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="h6" color="text.secondary">No inventory items found</Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.15)}`,
        overflow: 'hidden'
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={thCellSx}>Expand</TableCell>
              <TableCell sx={thCellSx}>Type</TableCell>
              <TableCell sx={thCellSx}>Item</TableCell>
              <TableCell sx={thCellSx}>Quantity</TableCell>
              <TableCell sx={thCellSx}>Location</TableCell>
              <TableCell sx={thCellSx}>Reorder Settings</TableCell>
              <TableCell sx={thCellSx} align="center">Status</TableCell>
              <TableCell sx={thCellSx} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, idx) => {
              const key       = rowKey(item);
              const isExp     = !!expandedRows[key];
              const lotItems  = item.lots || [];
              const qty       = item.total_quantity || 0;
              const rp        = item.reorder_point || 0;
              const rqty      = item.reorder_quantity || 0;
              const isLow     = rp > 0 && qty > 0 && qty <= rp;
              const isOut     = qty <= 0;
              const typeSt    = TYPE_STYLES[item._inventoryType] || TYPE_STYLES.supplies;

              const isProduct    = item._inventoryType === 'products';
              const prevItem     = data[idx - 1];
              const curBrand     = item.brand_name || null;
              const prevBrand    = prevItem?.brand_name || null;
              const showBrandHeader = isProduct && (
                !prevItem ||
                prevItem._inventoryType !== 'products' ||
                prevBrand !== curBrand
              );
              const showTypeHeader = !isProduct && (
                !prevItem ||
                prevItem._inventoryType !== item._inventoryType
              );

              return (
                <React.Fragment key={`${key}-${idx}`}>
                  {showBrandHeader && (() => {
                    const curBrandId = item.brand_id;
                    const brandIdx = sortedBrandsForReorder.findIndex(b => b.id === curBrandId);
                    const isFirst = brandIdx <= 0;
                    const isLast = brandIdx >= sortedBrandsForReorder.length - 1;
                    return (
                      <TableRow>
                        <TableCell colSpan={8} sx={{
                          bgcolor: alpha('#e3f2fd', 0.5),
                          borderTop: `2px solid ${alpha('#1565c0', 0.2)}`,
                          borderBottom: `1px solid ${alpha('#1565c0', 0.15)}`,
                          py: 1.5, px: 3
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontWeight: 400, color: '#1565c0', fontSize: '0.85rem' }}>
                              {curBrand || 'Unbranded'}
                            </Typography>
                            {curBrandId && (
                              <Stack direction="row" spacing={0.5}>
                                <span>
                                  <IconButton size="small" disabled={isFirst}
                                    onClick={() => handleBrandReorder(curBrandId, 'up')}
                                    sx={{ color: isFirst ? 'text.disabled' : '#1565c0' }}>
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                                <span>
                                  <IconButton size="small" disabled={isLast}
                                    onClick={() => handleBrandReorder(curBrandId, 'down')}
                                    sx={{ color: isLast ? 'text.disabled' : '#1565c0' }}>
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Stack>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                  {showTypeHeader && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{
                        bgcolor: alpha(typeSt.bg, 0.5),
                        borderBottom: `1px solid ${alpha(typeSt.color, 0.15)}`,
                        borderTop: `2px solid ${alpha(typeSt.color, 0.2)}`,
                        py: 1.5, px: 3
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 400, color: typeSt.color, fontSize: '0.85rem' }}>
                          {typeSt.label}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Aggregate row */}
                  <TableRow hover sx={{
                    cursor: 'pointer', transition: 'all 0.2s ease', bgcolor: 'white',
                    '&:hover': { bgcolor: 'grey.50', transform: 'translateX(4px)', boxShadow: 'inset 4px 0 0 black' },
                    '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}` }
                  }}>
                    <TableCell sx={tdCellSx}>
                      <IconButton size="small" onClick={() => toggleRow(key)}>
                        {isExp ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={tdCellSx}>
                      <Chip label={typeSt.label} size="small" sx={{
                        bgcolor: typeSt.bg, color: typeSt.color, fontWeight: 500,
                        border: `1px solid ${typeSt.color}55`, fontSize: '0.75rem'
                      }} />
                    </TableCell>
                    <TableCell sx={tdCellSx}>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', mb: 0.5 }}>
                        {item.item_name}
                      </Typography>
                      {item.item_sku && (
                        <Typography variant="caption" color="text.secondary">SKU: {item.item_sku}</Typography>
                      )}
                      {item.variant_name && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {item.variant_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tdCellSx}>
                      <ItemQuantity item={item} />
                    </TableCell>
                    <TableCell sx={tdCellSx}>
                      <Typography variant="body2">{item.location_name}</Typography>
                    </TableCell>
                    <TableCell sx={tdCellSx}>
                      <Typography variant="body2" sx={{ mb: isProduct ? 0 : 0.5 }}>Reorder Point: {rp.toLocaleString()}</Typography>
                      {!isProduct && <Typography variant="body2">Reorder Qty: {rqty.toLocaleString()}</Typography>}
                    </TableCell>
                    <TableCell sx={tdCellSx} align="center">
                      {isLow ? (
                        <Chip label="Low Stock" size="small" color="warning" icon={<WarningIcon />} />
                      ) : isOut ? (
                        <Chip label="Out of Stock" size="small" color="error" />
                      ) : (
                        <Chip label="In Stock" size="small" color="success" icon={<CheckCircleIcon />} />
                      )}
                    </TableCell>
                    <TableCell sx={tdCellSx} align="center">
                      <IconButton size="small" color="primary" disabled={!canEdit}
                        onClick={() => { setSelectedItem(item); setEditReorderOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" disabled={!canDelete} onClick={() => handleDeleteAgg(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Lot/batch expansion row */}
                  <TableRow>
                    <TableCell sx={{ py: 0, px: 0, border: 'none' }} colSpan={8}>
                      <Collapse in={isExp} timeout="auto" unmountOnExit>
                        <Box sx={{
                          p: 4, bgcolor: 'white',
                          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
                        }}>
                          {/* Lot header */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
                              Lot/Batch Details ({lotItems.length} lot{lotItems.length !== 1 ? 's' : ''})
                            </Typography>
                            <TextField placeholder="Search lot/batch..." size="small"
                              value={lotSearchTerms[key] || ''}
                              onChange={(e) => setLotSearchTerms(prev => ({ ...prev, [key]: e.target.value }))}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                  </InputAdornment>
                                )
                              }}
                              sx={{
                                width: { xs: '100%', sm: '400px' },
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2, bgcolor: 'white',
                                  '& fieldset': { borderColor: alpha(theme.palette.grey[400], 0.8) },
                                  '&:hover fieldset': { borderColor: 'primary.main' }
                                },
                                '& .MuiInputBase-input': { fontSize: '0.9rem', py: 1.5 }
                              }}
                            />
                          </Box>

                          {/* Lot table */}
                          {(() => {
                            const cfg      = item._config;
                            const search   = (lotSearchTerms[key] || '').toLowerCase();
                            const filtered = search
                              ? lotItems.filter(l =>
                                  (l.lot_number || '').toLowerCase().includes(search) ||
                                  (l.batch_number || '').toLowerCase().includes(search))
                              : lotItems;
                            const sorted = [...filtered].sort((a, b) => {
                              const da = a.manufactured_date ? new Date(a.manufactured_date) : new Date(0);
                              const db = b.manufactured_date ? new Date(b.manufactured_date) : new Date(0);
                              return db - da;
                            });
                            const lbPage = lotPagination[key]?.page ?? 0;
                            const lbRpp  = lotPagination[key]?.rowsPerPage ?? LOT_BATCH_DEFAULT_ROWS;
                            const paged  = sorted.slice(lbPage * lbRpp, (lbPage + 1) * lbRpp);

                            const lotThSx = { fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 };
                            const lotRowSx = {
                              transition: 'all 0.2s ease',
                              '&:hover': { bgcolor: 'grey.50', transform: 'translateX(4px)', boxShadow: 'inset 4px 0 0 black' },
                              '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.grey[50], 0.3) }
                            };

                            if (cfg.hasUnitConfig) {
                              /* ── Products: Quantity | Cases | Lot/Batch Details | Dates | Status | Documents | Actions ── */
                              return (
                                <TableContainer component={Paper} sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, overflow: 'hidden' }}>
                                  <Table>
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                                        {['Quantity','Cases','Lot/Batch Details','Dates','Status','Documents','Actions'].map(h => (
                                          <TableCell key={h} align={['Status','Documents','Actions'].includes(h) ? 'center' : 'left'} sx={lotThSx}>
                                            {h}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {paged.map((lot, li) => {
                                        const now = new Date(); now.setHours(0, 0, 0, 0);
                                        const expDate = lot.expiry_date ? (() => { const [y,m,d] = lot.expiry_date.split('-').map(Number); return new Date(y, m-1, d); })() : null;
                                        const daysToExp = expDate ? Math.ceil((expDate - now) / 86400000) : null;
                                        const isExpired = expDate && expDate <= now;
                                        const isExpiringSoon = !isExpired && daysToExp != null && daysToExp <= 30;
                                        return (
                                          <TableRow key={`lot-${lot.id}-${li}`} hover sx={lotRowSx}>
                                            {/* Quantity */}
                                            <TableCell sx={tdCellSx}>
                                              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
                                                {(lot.total_base_units ?? 0).toLocaleString()} units
                                              </Typography>
                                              {(lot.enable_pallets ?? false) && (lot.pallet_quantity || 0) > 0 && (
                                                <Box sx={{ mb: 0.5 }}>
                                                  <Chip
                                                    label={`${lot.pallet_quantity} ${lot.pallet_quantity === 1 ? 'Pallet' : 'Pallets'}`}
                                                    size="medium"
                                                    sx={{ fontSize: '0.8rem', height: 24, fontWeight: 400 }}
                                                  />
                                                </Box>
                                              )}
                                              <Tooltip title={`This lot uses: ${lot.units_per_case || 1} units/case, ${lot.cases_per_pallet || 1} cases/pallet`}>
                                                <Chip
                                                  label={`Config: ${lot.units_per_case || 1}/${lot.cases_per_pallet || 1}`}
                                                  size="small" variant="outlined" color="info"
                                                  sx={{ fontSize: '0.75rem', height: 22, cursor: 'help' }}
                                                />
                                              </Tooltip>
                                            </TableCell>
                                            {/* Cases */}
                                            <TableCell sx={tdCellSx}>
                                              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
                                                {Math.floor((lot.total_base_units ?? 0) / (lot.units_per_case || 1)).toLocaleString()} cases
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                {(lot.total_base_units ?? 0).toLocaleString()} total units
                                              </Typography>
                                            </TableCell>
                                            {/* Lot/Batch Details */}
                                            <TableCell sx={tdCellSx}>
                                              {lot.lot_number && (
                                                <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                                                  Lot: {lot.lot_number}
                                                </Typography>
                                              )}
                                              {lot.batch_number && (
                                                <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                                                  Batch: {lot.batch_number}
                                                </Typography>
                                              )}
                                            </TableCell>
                                            {/* Dates */}
                                            <TableCell sx={tdCellSx}>
                                              {lot.manufactured_date && (
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                  Mfg: {formatDate(lot.manufactured_date)}
                                                </Typography>
                                              )}
                                              {lot.expiry_date && (
                                                <Typography variant="body2" color={isExpired ? 'error.main' : isExpiringSoon ? 'warning.main' : 'text.secondary'}>
                                                  Exp: {formatDate(lot.expiry_date)}
                                                </Typography>
                                              )}
                                            </TableCell>
                                            {/* Status */}
                                            <TableCell sx={tdCellSx} align="center">
                                              {isExpired ? (
                                                <Chip label="Expired" size="small" color="error" icon={<WarningIcon />} />
                                              ) : isExpiringSoon ? (
                                                <Chip label="Expiring Soon" size="small" color="warning" icon={<WarningIcon />} />
                                              ) : (
                                                <Chip
                                                  label={lot.is_active ? 'Active' : 'Inactive'}
                                                  size="small"
                                                  color={lot.is_active ? 'success' : 'default'}
                                                  icon={lot.is_active ? <CheckCircleIcon /> : undefined}
                                                />
                                              )}
                                            </TableCell>
                                            {/* Documents */}
                                            <TableCell sx={tdCellSx} align="center">
                                              {renderDocuments(lot, cfg)}
                                            </TableCell>
                                            {/* Actions */}
                                            <TableCell sx={tdCellSx} align="center">
                                              <IconButton size="small" color="primary" disabled={!canEdit}
                                                onClick={() => {
                                                  setSelectedItem({ ...lot, _config: cfg, _inventoryType: item._inventoryType });
                                                  setEditLotOpen(true);
                                                }}>
                                                <EditIcon fontSize="small" />
                                              </IconButton>
                                              <IconButton size="small" color="error" disabled={!canDelete}
                                                onClick={() => handleDeleteLot(lot, cfg)}>
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              );
                            }

                            /* ── Non-products: Quantity | Lot/Batch Details | Vendor & Invoice | Status | Documents | Actions ── */
                            return (
                              <TableContainer component={Paper} sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, overflow: 'hidden' }}>
                                <Table>
                                  <TableHead>
                                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                                      {['Quantity','Lot/Batch Details','Vendor & Invoice','Status','Documents','Actions'].map(h => (
                                        <TableCell key={h} align={['Status','Documents','Actions'].includes(h) ? 'center' : 'left'} sx={lotThSx}>
                                          {h}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {paged.map((lot, li) => {
                                      const now2 = new Date(); now2.setHours(0, 0, 0, 0);
                                      const expDate2 = lot.expiry_date ? (() => { const [y,m,d] = lot.expiry_date.split('-').map(Number); return new Date(y, m-1, d); })() : null;
                                      const daysToExp2 = expDate2 ? Math.ceil((expDate2 - now2) / 86400000) : null;
                                      const isExpired2 = expDate2 && expDate2 <= now2;
                                      const isExpiringSoon2 = !isExpired2 && daysToExp2 != null && daysToExp2 <= 30;
                                      return (
                                      <TableRow key={`lot-${lot.id}-${li}`} hover sx={lotRowSx}>
                                        <TableCell sx={tdCellSx}>
                                          <LotQuantity lotItem={lot} cfg={cfg} parentItem={item} />
                                        </TableCell>
                                        <TableCell sx={tdCellSx}>
                                          {lot.lot_number && (
                                            <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                                              Lot: {lot.lot_number}
                                            </Typography>
                                          )}
                                          {lot.batch_number && (
                                            <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                                              Batch: {lot.batch_number}
                                            </Typography>
                                          )}
                                          {lot.expiry_date && (
                                            <Typography variant="caption" sx={{ display: 'block', color: isExpired2 ? 'error.main' : isExpiringSoon2 ? 'warning.main' : 'text.secondary' }}>
                                              Exp: {formatDate(lot.expiry_date)}
                                              {isExpired2 && ' (EXPIRED)'}
                                              {isExpiringSoon2 && ' (Soon)'}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell sx={tdCellSx}>
                                          {lot.vendor_info?.supplier_name && (
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem', mb: 0.5, fontWeight: 500 }}>
                                              {lot.vendor_info.supplier_name}
                                            </Typography>
                                          )}
                                          {lot.vendor_info?.unit_cost && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              Cost: ${parseFloat(lot.vendor_info.unit_cost).toFixed(2)}
                                            </Typography>
                                          )}
                                          {lot.vendor_info?.invoice_number && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              Invoice #: {lot.vendor_info.invoice_number}
                                            </Typography>
                                          )}
                                          {lot.vendor_info?.invoice_date && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              Invoice Date: {formatDate(lot.vendor_info.invoice_date)}
                                            </Typography>
                                          )}
                                          {lot.vendor_info?.po_number && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              PO #: {lot.vendor_info.po_number}
                                            </Typography>
                                          )}
                                          {lot.vendor_info?.po_date && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              PO Date: {formatDate(lot.vendor_info.po_date)}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell sx={tdCellSx} align="center">
                                          {isExpired2 ? (
                                            <Chip label="Expired" size="small" color="error" icon={<WarningIcon />} />
                                          ) : isExpiringSoon2 ? (
                                            <Chip label="Expiring Soon" size="small" color="warning" icon={<WarningIcon />} />
                                          ) : (
                                            <Chip
                                              label={lot.is_active ? 'Active' : 'Inactive'}
                                              size="small"
                                              color={lot.is_active ? 'success' : 'default'}
                                              icon={lot.is_active ? <CheckCircleIcon /> : undefined}
                                            />
                                          )}
                                        </TableCell>
                                        <TableCell sx={tdCellSx} align="center">
                                          {renderDocuments(lot, cfg)}
                                        </TableCell>
                                        <TableCell sx={tdCellSx} align="center">
                                          <IconButton size="small" color="primary" disabled={!canEdit}
                                            onClick={() => {
                                              setSelectedItem({
                                                ...lot,
                                                _config: cfg,
                                                _inventoryType: item._inventoryType,
                                                _unitType: lot.unit_type || item.unit_type,
                                                _unitMeasure: lot.unit_measure || item.unit_measure,
                                                _unitQtyPerUnit: lot.unit_qty_per_unit || item.unit_qty_per_unit,
                                              });
                                              setEditLotOpen(true);
                                            }}>
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton size="small" color="error" disabled={!canDelete}
                                            onClick={() => handleDeleteLot(lot, cfg)}>
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            );
                          })()}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Lot Dialog */}
      {editLotOpen && selectedItem?._config && (
        <EditLotDialog
          open={editLotOpen}
          onClose={() => { setEditLotOpen(false); setSelectedItem(null); }}
          item={selectedItem}
          config={selectedItem._config}
          inventoryType={selectedItem._inventoryType}
          onSuccess={async () => { setEditLotOpen(false); setSelectedItem(null); if (onRefresh) await onRefresh(); }}
          canDelete={canDelete}
        />
      )}

      {/* Edit Reorder Dialog */}
      {editReorderOpen && selectedItem?._config && (
        <EditReorderDialog
          open={editReorderOpen}
          onClose={() => { setEditReorderOpen(false); setSelectedItem(null); }}
          item={selectedItem}
          config={selectedItem._config}
          inventoryType={selectedItem._inventoryType}
          onSuccess={async () => { setEditReorderOpen(false); setSelectedItem(null); if (onRefresh) await onRefresh(); }}
        />
      )}

      {/* Delete Lot Confirmation */}
      <Dialog open={deleteLotOpen} onClose={() => !deleting && setDeleteLotOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Lot</DialogTitle>
        <DialogContent>
          <Typography>Delete this lot? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLotOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={confirmDeleteLot} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Aggregate Confirmation */}
      <Dialog open={deleteAggOpen} onClose={() => !deleting && setDeleteAggOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete All Lots</DialogTitle>
        <DialogContent>
          <Typography>Delete all lots for <strong>{itemToDelete?.item_name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAggOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={confirmDeleteAgg} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
